<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card elevation="3">
          <v-card-title class="text-center bg-primary">
            <v-icon class="me-2">mdi-bank-transfer</v-icon>
            CMS 자동이체 테스트
          </v-card-title>
          
          <v-card-text class="pa-6">
            <!-- 회원 ID 입력 -->
            <v-row>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="memberId"
                  label="회원 ID"
                  prepend-icon="mdi-account"
                  variant="outlined"
                  hint="테스트할 회원의 고유 ID를 입력하세요"
                />
              </v-col>
              <v-col cols="12" sm="4" class="d-flex align-center">
                <v-btn
                  @click="getCmsMember"
                  :loading="waitStatus"
                  color="primary"
                  block
                  prepend-icon="mdi-account-search"
                >
                  회원 조회
                </v-btn>
              </v-col>
            </v-row>

            <!-- 회원 조회 결과 -->
            <v-card v-if="cmsMembers.length > 0" variant="outlined" class="mt-4">
              <v-card-title class="d-flex align-center">
                <v-icon class="me-2">mdi-account-group</v-icon>
                회원 정보
                <v-spacer />
                <v-btn
                  color="error"
                  variant="tonal"
                  prepend-icon="mdi-account-remove"
                  @click="confirmDelete"
                  :loading="waitStatus"
                >
                  회원 해지
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-row v-for="(member, index) in cmsMembers" :key="index">
                  <v-col cols="12">
                    <v-card variant="outlined" class="pa-4">
                      <!-- 기본 정보 -->
                      <v-card-title class="section-title">
                        <v-icon class="me-2">mdi-account</v-icon>
                        기본 정보
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">회원명</div>
                            <div class="info-value">{{ member?.memberInfo?.memberName || '-' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">서비스코드</div>
                            <div class="info-value">{{ member?.memberInfo?.serviceCd || '-' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">상품명/서비스명</div>
                            <div class="info-value">{{ member?.memberInfo?.serviceName || '-' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">휴대폰번호</div>
                            <div class="info-value">{{ member?.memberInfo?.hpNo || '-' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">이메일</div>
                            <div class="info-value">{{ member?.memberInfo?.email || '-' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">사용자정의 필드</div>
                            <div class="info-value">{{ member?.memberInfo?.userDefine || '-' }}</div>
                          </v-col>
                        </v-row>
                      </v-card-text>

                      <!-- 계좌/카드 정보 -->
                      <v-card-title class="section-title">
                        <v-icon class="me-2">{{ member?.memberInfo?.serviceCd === 'BANK' ? 'mdi-bank' : 'mdi-credit-card' }}</v-icon>
                        {{ member?.memberInfo?.serviceCd === 'BANK' ? '계좌' : '카드' }} 정보
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <template v-if="member?.memberInfo?.serviceCd === 'BANK'">
                            <v-col cols="12" sm="6" md="4">
                              <div class="info-label">은행코드</div>
                              <div class="info-value">{{ member?.memberInfo?.bankCd || '-' }}</div>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <div class="info-label">출금계좌번호</div>
                              <div class="info-value">{{ member?.memberInfo?.accountNo || '-' }}</div>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <div class="info-label">출금계좌 예금주명</div>
                              <div class="info-value">{{ member?.memberInfo?.accountName || '-' }}</div>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <div class="info-label">생년월일/사업자번호</div>
                              <div class="info-value">{{ member?.memberInfo?.idNo || '-' }}</div>
                            </v-col>
                          </template>
                          <template v-else>
                            <v-col cols="12" sm="6" md="4">
                              <div class="info-label">카드번호</div>
                              <div class="info-value">{{ member?.memberInfo?.cardNo || '-' }}</div>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <div class="info-label">카드 유효기간</div>
                              <div class="info-value">{{ member?.memberInfo?.valYn || '-' }}</div>
                            </v-col>
                          </template>
                        </v-row>
                      </v-card-text>

                      <!-- 현금영수증 정보 -->
                      <v-card-title class="section-title">
                        <v-icon class="me-2">mdi-receipt</v-icon>
                        현금영수증 정보
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">발행타입</div>
                            <div class="info-value">{{ member?.memberInfo?.cusType === '1' ? '소득공제' : '지출증빙' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">인증번호</div>
                            <div class="info-value">{{ member?.memberInfo?.cusOffNo || '-' }}</div>
                          </v-col>
                        </v-row>
                      </v-card-text>

                      <!-- 상태 정보 -->
                      <v-card-title class="section-title">
                        <v-icon class="me-2">mdi-information</v-icon>
                        상태 정보
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">회원상태</div>
                            <div class="info-value">
                              <v-chip
                                :color="getStatusColor(member?.memberInfo?.status)"
                                size="small"
                              >
                                {{ getStatusText(member?.memberInfo?.status) }}
                              </v-chip>
                            </div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">은행등록 결과</div>
                            <div class="info-value">{{ member?.memberInfo?.bankResultMsg || '-' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">회원등록일자</div>
                            <div class="info-value">{{ member?.memberInfo?.regDt || '-' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">은행등록일자</div>
                            <div class="info-value">{{ member?.memberInfo?.bankSendDt || '-' }}</div>
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <div class="info-label">회원해지일자</div>
                            <div class="info-value">{{ member?.memberInfo?.stopDt || '-' }}</div>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- 출금동의 증빙파일 업로드 -->
            <v-card v-if="cmsMembers.length > 0" variant="outlined" class="mt-4">
              <v-card-title class="d-flex align-center">
                <v-icon class="me-2">mdi-file-upload</v-icon>
                출금동의 증빙파일 업로드
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="fileForm.agreeType"
                      label="증빙파일 구분 *"
                      variant="outlined"
                      density="compact"
                      :items="[
                        { title: '서면', value: '1' },
                        { title: '전자서명', value: '2' },
                        { title: '녹취', value: '4' }
                      ]"
                      hint="1: 서면, 2: 전자서명, 4: 녹취"
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="fileForm.uploadMethod"
                      label="업로드 방식 *"
                      variant="outlined"
                      density="compact"
                      :items="[
                        { title: 'Multipart (권장)', value: 'multipart' },
                        { title: 'Base64', value: 'base64' }
                      ]"
                      hint="업로드 방식을 선택하세요"
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-file-input
                      v-model="fileForm.file"
                      label="증빙파일 선택 *"
                      variant="outlined"
                      density="compact"
                      :accept="getAcceptedFileTypes()"
                      prepend-icon="mdi-file-document"
                      show-size
                      @change="onFileChange"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-alert
                      variant="tonal"
                      color="info"
                      class="mb-4"
                    >
                      <template v-slot:prepend>
                        <v-icon icon="mdi-information" />
                      </template>
                      <div class="text-subtitle-2 mb-2">파일 업로드 규칙</div>
                      <ul class="text-body-2">
                        <li><strong>서면:</strong> jpg, jpeg, gif, tif, pdf (최대 300KB)</li>
                        <li><strong>녹취:</strong> mp3, wav, wma (최대 300KB)</li>
                        <li><strong>전자서명:</strong> der (최대 10KB)</li>
                        <li><strong>파일명 규칙:</strong> 이용기관코드.회원아이디.증빙자료구분.확장자</li>
                      </ul>
                    </v-alert>
                  </v-col>
                  <v-col cols="12" class="text-center">
                    <v-btn
                      @click="uploadAgreeFile"
                      :loading="fileUploadStatus"
                      color="success"
                      prepend-icon="mdi-upload"
                      :disabled="!fileForm.file || !fileForm.agreeType || !fileForm.uploadMethod"
                    >
                      증빙파일 업로드 ({{ fileForm.uploadMethod === 'base64' ? 'Base64' : 'Multipart' }})
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- 삭제 확인 다이얼로그 -->
            <v-dialog v-model="deleteDialog" max-width="400">
              <v-card>
                <v-card-title class="text-h5">
                  회원 해지 확인
                </v-card-title>
                <v-card-text>
                  정말로 이 회원을 해지하시겠습니까?
                  <br>
                  회원 ID: {{ memberId }}
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="grey-darken-1"
                    variant="text"
                    @click="deleteDialog = false"
                  >
                    취소
                  </v-btn>
                  <v-btn
                    color="error"
                    variant="text"
                    @click="deleteMember"
                    :loading="waitStatus"
                  >
                    해지
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- 테스트 데이터 폼 -->
            <v-expansion-panels class="mb-4">
              <v-expansion-panel>
                <v-expansion-panel-title>
                  <v-icon class="me-2">mdi-form-select</v-icon>
                  테스트 데이터 설정
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.memberName"
                        label="회원명 *"
                        variant="outlined"
                        density="compact"
                        hint="20byte 이내"
                        maxlength="20"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-select
                        v-model="form.serviceCd"
                        label="서비스코드 *"
                        variant="outlined"
                        density="compact"
                        :items="[
                          { title: '은행 (BANK)', value: 'BANK' },
                          { title: '카드 (CARD)', value: 'CARD' }
                        ]"
                        hint="은행: BANK, 카드: CARD"
                      />
                    </v-col>
                    
                    <!-- 은행 관련 필드들 (serviceCd가 BANK인 경우에만 표시) -->
                    <template v-if="form.serviceCd === 'BANK'">
                      <v-col cols="12" sm="6">
                        <v-select
                          v-model="form.bankCd"
                          label="은행코드 *"
                          variant="outlined"
                          density="compact"
                          :items="bankCodes"
                          item-title="bank_name"
                          item-value="bank_code"
                          hint="은행을 선택하세요"
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="form.accountNo"
                          label="출금계좌번호 *"
                          variant="outlined"
                          density="compact"
                          hint="16byte 이내"
                          maxlength="16"
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="form.accountName"
                          label="출금계좌 예금주명 *"
                          variant="outlined"
                          density="compact"
                          hint="20byte 이내"
                          maxlength="20"
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="form.idNo"
                          label="생년월일 또는 사업자번호 *"
                          variant="outlined"
                          density="compact"
                          hint="생년월일 앞6자리 또는 사업자번호 10자리"
                          maxlength="10"
                        />
                      </v-col>
                    </template>
                    
                    <!-- 카드 관련 필드들 (serviceCd가 CARD인 경우에만 표시) -->
                    <template v-if="form.serviceCd === 'CARD'">
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="form.cardNo"
                          label="카드번호 *"
                          variant="outlined"
                          density="compact"
                          hint="16byte 이내"
                          maxlength="16"
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="form.valYn"
                          label="카드 유효기간 *"
                          variant="outlined"
                          density="compact"
                          hint="YYMM 형식 (예: 2312)"
                          maxlength="4"
                          placeholder="YYMM"
                        />
                      </v-col>
                    </template>
                    
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.hpNo"
                        label="휴대폰번호 *"
                        variant="outlined"
                        density="compact"
                        hint="하이픈 없이 입력 (16byte 이내)"
                        maxlength="16"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.email"
                        label="이메일"
                        variant="outlined"
                        density="compact"
                        type="email"
                        hint="30byte 이내"
                        maxlength="30"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.serviceName"
                        label="상품명/서비스명"
                        variant="outlined"
                        density="compact"
                        hint="30byte 이내"
                        maxlength="30"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-select
                        v-model="form.cusType"
                        label="현금영수증 발행타입"
                        variant="outlined"
                        density="compact"
                        :items="[
                          { title: '소득공제 (1)', value: '1' },
                          { title: '지출증빙 (2)', value: '2' }
                        ]"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.cusOffNo"
                        label="현금영수증 인증번호"
                        variant="outlined"
                        density="compact"
                        hint="휴대폰번호 또는 현금영수증카드번호/사업자번호"
                        maxlength="20"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.userDefine"
                        label="사용자정의 필드"
                        variant="outlined"
                        density="compact"
                        hint="20byte 이내"
                        maxlength="20"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
            
            <!-- 요청 버튼 -->
            <div class="text-center mb-4">
              <v-btn
                @click="sendRequest"
                :loading="waitStatus"
                color="primary"
                size="large"
                prepend-icon="mdi-send"
                :disabled="!memberId"
              >
                {{ waitStatus ? '요청 중...' : 'CMS API 요청 보내기' }}
              </v-btn>
            </div>
            
            <!-- 결과 표시 -->
            <v-card v-if="result" variant="outlined" class="mt-4">
              <v-card-title class="d-flex align-center">
                <v-icon 
                  :color="isSuccess ? 'success' : 'error'" 
                  class="me-2"
                >
                  {{ isSuccess ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                </v-icon>
                응답 결과
                <v-spacer />
                <v-chip 
                  :color="isSuccess ? 'success' : 'error'" 
                  size="small"
                >
                  {{ isSuccess ? '성공' : '실패' }}
                </v-chip>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12">
                    <v-alert
                      v-if="result?.data?.memberInfo"
                      :color="isSuccess ? 'success' : 'error'"
                      variant="tonal"
                      class="mb-4"
                    >
                      <template v-slot:prepend>
                        <v-icon :icon="isSuccess ? 'mdi-check-circle' : 'mdi-alert-circle'" />
                      </template>
                      <div class="text-subtitle-1 font-weight-bold mb-2">회원 정보</div>
                      <div class="d-flex flex-column gap-2">
                        <div><strong>회원상태:</strong> {{ getStatusText(result.data.memberInfo.status) }}</div>
                        <div><strong>은행등록 결과:</strong> {{ result.data.memberInfo.bankResultMsg || '-' }}</div>
                        <div><strong>회원등록일자:</strong> {{ result.data.memberInfo.regDt || '-' }}</div>
                        <div><strong>은행등록일자:</strong> {{ result.data.memberInfo.bankSendDt || '-' }}</div>
                        <div><strong>회원해지일자:</strong> {{ result.data.memberInfo.stopDt || '-' }}</div>
                      </div>
                    </v-alert>
                  </v-col>
                  <v-col cols="12">
                    <v-code class="d-block">
                      {{ JSON.stringify(result, null, 2) }}
                    </v-code>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 스낵바 알림 -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar.show = false"
        >
          닫기
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue'
import axios from 'axios'

interface CmsMemberInfo {
  accountName: string;
  accountNo: string;
  bankCd: string;
  bankResultCd: string;
  bankResultMsg: string;
  bankSendDt: string;
  cardNo: string;
  cusOffNo: string;
  cusType: string;
  email: string;
  hpNo: string;
  idNo: string;
  memberName: string;
  regDt: string;
  serviceCd: string;
  serviceName: string;
  status: string;
  stopDt: string;
  userDefine: string;
  valYn: string;
}

interface CmsResponse {
  memberInfo: CmsMemberInfo;
  resultCd: string;
  resultMsg: string;
}

const waitStatus = ref(false)
const memberId = ref('testuser01')
const bankCodes = ref([])  // 은행 코드 목록을 저장할 상태 추가
const cmsMembers = ref<CmsResponse[]>([])  // CMS 회원 목록을 저장할 상태 추가
const form = ref({
  memberName: '홍길동',        // 회원 성명 (20byte, 필수) - 마스킹 처리됨
  serviceCd: 'BANK',          // 서비스코드 (4byte, 필수) - 은행:BANK, 카드:CARD
  bankCd: '002',              // 은행코드 (3byte) - serviceCd가 BANK인 경우 필수
  accountNo: '12345678901234', // 출금계좌번호 (16byte) - serviceCd가 BANK인 경우 필수, 마스킹 처리됨
  accountName: '홍길동',       // 출금계좌 예금주 성명 (20byte) - serviceCd가 BANK인 경우 필수, 마스킹 처리됨
  idNo: '900101',             // 출금계좌 예금주 생년월일 앞6자리 또는 사업자번호 10자리 (10byte) - serviceCd가 BANK인 경우 필수, 마스킹 처리됨
  hpNo: '01012345678',        // 회원 휴대폰번호 (16byte, 필수)
  email: 'hong@example.com',  // 이메일 주소 (30byte)
  serviceName: '자동이체서비스', // 상품명/서비스명 (30byte)
  cardNo: '',                 // 카드번호 (16byte) - serviceCd가 CARD인 경우 필수, 마스킹 처리됨
  valYn: '',                  // 카드 유효기간 YYMM (4byte) - serviceCd가 CARD인 경우 필수
  cusType: '1',               // 현금영수증 발행타입 (1byte) - 소득공제:1, 지출증빙:2
  cusOffNo: '01012345678',    // 현금영수증 발행 시 인증번호 (20byte)
  userDefine: '테스트회원'     // 사용자정의 필드 (20byte)
})

const result = ref<any>(null)

// 스낵바 상태
const snackbar = reactive({
  show: false,
  text: '',
  color: 'info'
})

const isSuccess = computed(() => {
  return result.value && result.value.success === true
})

// 스낵바 표시 함수
const showSnackbar = (text: string, color: any) => {
  snackbar.text = text
  snackbar.color = color
  snackbar.show = true
}

const sendRequest = async () => {
  try {
    waitStatus.value = true
    result.value = null
    const res = await axios.post('/api/cms/register', {
      memberId: memberId.value,
      data: form.value
    })
    
    result.value = res.data
    
    if (result.value.success) {
      showSnackbar('CMS API 요청이 성공했습니다!', 'success');
    } else {
      const errorMsg = result.value.error?.message || 'API 요청 실패';
      const statusCode = result.value.statusCode || res.status;
      
      console.error('=== API 오류 상세 ===');
      console.error('Status Code:', statusCode);
      console.error('Error:', result.value.error);
      console.error('Message:', result.value.message);
      console.error('==================');
      
      showSnackbar(`API 오류 (${statusCode}): ${errorMsg}`, 'error');
    }
  } catch (err: any) {
    result.value = err.response?.data || { success: false, error: err.message }
    
    console.error('=== 네트워크 오류 ===');
    console.error('Error:', err);
    console.error('==================');
    
    showSnackbar(`네트워크 오류: ${err.message}`, 'error');
  } finally {
    waitStatus.value = false
  }
}

const getBankCodes = async () => {
  try {
    const response = await axios.get('/api/cms/getBankCodes');
    if (response.data.success) {
      bankCodes.value = response.data.data;
    } else {
      showSnackbar('은행 코드 목록을 가져오는데 실패했습니다.', 'error');
    }
  } catch (error) {
    console.error('은행 코드 조회 오류:', error);
    showSnackbar('은행 코드 목록을 가져오는데 실패했습니다.', 'error');
  }
};

const getCmsMember = async () => {
  try {
    waitStatus.value = true;
    const response = await axios.get('/api/cms/getCmsMember', {
      params: { memberId: memberId.value }
    });
    
    if (response.data.success) {
      // API 응답 데이터 구조에 맞게 수정
      cmsMembers.value = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
      showSnackbar('회원 조회가 완료되었습니다.', 'success');
    } else {
      showSnackbar('회원 조회에 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('회원 조회 오류:', error);
    showSnackbar(`회원 조회 오류: ${error.message}`, 'error');
  } finally {
    waitStatus.value = false;
  }
};

const deleteDialog = ref(false)

const confirmDelete = () => {
  deleteDialog.value = true
}

const deleteMember = async () => {
  try {
    waitStatus.value = true
    const response = await axios.delete(`/api/cms/members/${memberId.value}`)
    
    if (response.data.success) {
      
      console.log(response.data.data.resultCd,'response.data.resultCd')
      if(response.data.data.resultCd === '0000'){
        showSnackbar('회원이 성공적으로 해지되었습니다.', 'success')
        deleteDialog.value = false
        // 회원 목록 새로고침
        await getCmsMember()
      }else if(response.data.data.resultCd === '3005'){
        showSnackbar('회원이 이미 해지되었습니다.', 'error')
      }else{
        showSnackbar('회원 해지에 실패했습니다.', 'error')
      }
    } else {
      showSnackbar('회원 해지에 실패했습니다.', 'error')
    }
  } catch (error: any) {
    console.error('회원 해지 오류:', error)
    showSnackbar(`회원 해지 오류: ${error.message}`, 'error')
  } finally {
    waitStatus.value = false
  }
}

onMounted(() => {
  getBankCodes();
  getCmsMember();
});

// 회원상태 텍스트 변환 함수 수정
const getStatusText = (status: string) => {
  const statusMap: { [key: string]: string } = {
    '0': '등록대기',
    '1': '정상 등록',
    '2': '등록실패',
    '3': '해지'
  };
  return statusMap[status] || status;
};

// 회원상태에 따른 색상 반환 함수 추가
const getStatusColor = (status: string) => {
  const colorMap: { [key: string]: string } = {
    '0': 'warning',
    '1': 'success',
    '2': 'error',
    '3': 'grey'
  };
  return colorMap[status] || 'grey';
};

// 파일 업로드 관련 로직
const fileForm = ref({
  agreeType: '',
  uploadMethod: 'multipart',
  file: null
})

const fileUploadStatus = ref(false)

const getAcceptedFileTypes = () => {
  if (fileForm.value.agreeType === '1') {
    // 서면: jpg, jpeg, gif, tif, pdf
    return '.jpg,.jpeg,.gif,.tif,.pdf'
  } else if (fileForm.value.agreeType === '2') {
    // 전자서명: der
    return '.der'
  } else if (fileForm.value.agreeType === '4') {
    // 녹취: mp3, wav, wma
    return '.mp3,.wav,.wma'
  }
  return ''
}

const onFileChange = (event: Event) => {
  const files = fileForm.value.file as File[] | null
  const file = files?.[0]
  if (!file) return

  // 파일 크기 체크
  const maxSize = fileForm.value.agreeType === '2' ? 10 * 1024 : 300 * 1024 // 전자서명: 10KB, 나머지: 300KB
  
  if (file.size > maxSize) {
    const maxSizeText = fileForm.value.agreeType === '2' ? '10KB' : '300KB'
    showSnackbar(`파일 크기가 ${maxSizeText}를 초과합니다.`, 'error')
    fileForm.value.file = null
    return
  }

  // 파일 확장자 체크
  const fileExt = file.name.split('.').pop()?.toLowerCase()
  const allowedExts = getAllowedExtensions()
  
  if (!allowedExts.includes(fileExt || '')) {
    showSnackbar(`허용되지 않는 파일 형식입니다. (${allowedExts.join(', ')})`, 'error')
    fileForm.value.file = null
    return
  }
}

const getAllowedExtensions = () => {
  if (fileForm.value.agreeType === '1') {
    return ['jpg', 'jpeg', 'gif', 'tif', 'pdf']
  } else if (fileForm.value.agreeType === '2') {
    return ['der']
  } else if (fileForm.value.agreeType === '4') {
    return ['mp3', 'wav', 'wma']
  }
  return []
}

const uploadAgreeFile = async () => {
  try {
    fileUploadStatus.value = true
    
    if (!fileForm.value.file || !fileForm.value.agreeType || !fileForm.value.uploadMethod) {
      showSnackbar('파일과 증빙파일 구분을 선택해주세요.', 'error')
      return
    }

    const files = fileForm.value.file as File[] | null
    const file = files?.[0]
    if (!file) {
      showSnackbar('파일을 선택해주세요.', 'error')
      return
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase() || ''

    if (fileForm.value.uploadMethod === 'multipart') {
      // Multipart 방식
      const formData = new FormData()
      
      formData.append('agreetype', fileForm.value.agreeType)
      formData.append('fileext', fileExt)
      formData.append('file', file)
      
      const response = await axios.post(`/api/cms/uploadAgreeFile/${memberId.value}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      if (response.data.success) {
        showSnackbar('증빙파일이 성공적으로 업로드되었습니다. (Multipart)', 'success')
      } else {
        showSnackbar('증빙파일 업로드에 실패했습니다.', 'error')
      }
    } else {
      // Base64 방식
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const base64Data = (e.target?.result as string)?.split(',')[1] // data:mime;base64, 부분 제거
          
          const response = await axios.post(`/api/cms/uploadAgreeFileBase64/${memberId.value}`, {
            agreetype: fileForm.value.agreeType,
            fileext: fileExt,
            fileData: base64Data
          })
          
          if (response.data.success) {
            showSnackbar('증빙파일이 성공적으로 업로드되었습니다. (Base64)', 'success')
          } else {
            showSnackbar('증빙파일 업로드에 실패했습니다.', 'error')
          }
        } catch (error: any) {
          console.error('Base64 업로드 오류:', error)
          showSnackbar(`Base64 업로드 오류: ${error.message}`, 'error')
        }
      }
      reader.readAsDataURL(file)
    }
    
    // 폼 초기화
    fileForm.value = {
      agreeType: '',
      uploadMethod: 'multipart',
      file: null
    }
    
  } catch (error: any) {
    console.error('증빙파일 업로드 오류:', error)
    showSnackbar(`증빙파일 업로드 오류: ${error.message}`, 'error')
  } finally {
    fileUploadStatus.value = false
  }
}
</script>

<style scoped lang="scss">
.info-label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.info-value {
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.87);
  font-family: inherit;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  font-family: inherit;
  margin-bottom: 8px;
  padding: 0;
}

.v-card-title {
  font-family: inherit;
}

.v-card-text {
  font-family: inherit;
}

.v-chip {
  font-family: inherit;
}

.v-code {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 400px;
  overflow-y: auto;
}
</style>
